import {QrCodeScannedCallback, QrCodeScanner, ScannerHandle} from "./qrCodeScanner";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {Notify} from "@/shared/ui/notify";

// Basically a div with a video element and a button to retry to get camera permission if it was denied previously
const template = `
				<div>
					<video id="video" autoplay muted playsinline class="w-100"></video>
					<div id="noCameraPermissionContainer" style="display: none;" class="text-center">
						<p>
							${messages["qrCodeScanner.noCameraPermission"]}
						</p>
						<button id="retryButton" class="btn btn-default">${messages["dialog.retry"]}</button>
					</div>
			   	</div>`;

/**
 * The QrCodeScannerDialog class is responsible for displaying a dialog that allows users to scan QR codes.
 * It provides functionality to initialize the scanner, handle the scanning process, and return the scanned result.
 */
export class QrCodeScannerDialog extends HTMLElement {

	private qrCodeScannedCallback?: QrCodeScannedCallback;
	private scannerHandle?: ScannerHandle;
	private modal?: BootstrapModal.Modal;
	private scanner: QrCodeScanner;
	private noCameraPermissionContainer: HTMLElement;

	public static show(qrCodeScannedCallback: QrCodeScannedCallback) {
		const dialog = document.createElement("qr-code-scanner-dialog") as QrCodeScannerDialog;
		dialog.qrCodeScannedCallback = qrCodeScannedCallback;
		dialog.show();
	}

	// is called when the QrCodeScannerDialog (which is a Custom Element) is added to the DOM
	private async connectedCallback() {
		this.innerHTML = template;

		this.noCameraPermissionContainer = this.querySelector("#noCameraPermissionContainer");
		this.noCameraPermissionContainer.querySelector("#retryButton").addEventListener("click", async () => {
			// try to get camera permission again and show a message to the user if it fails
			try {
				await navigator.mediaDevices.getUserMedia({video: true});
				void this.initScanner();
			} catch {
				Notify.danger(messages["qrCodeScanner.noCameraPermission"]);
			}
		});
		try {
			await this.initScanner();
		} catch (err) {
			this.noCameraPermissionContainer.style.display = "block";
			console.error("Failed to initialize QR scanner:", err);
		}
	}

	private async initScanner() {
		this.scanner = new QrCodeScanner(this, "#video", (scannedCode => this.qrCodeScanned(scannedCode)));

		// start scanning
		this.scannerHandle = await this.scanner.start();
		this.noCameraPermissionContainer.style.display = "none";
	}

	private qrCodeScanned(scannedCode: string) {
		this.qrCodeScannedCallback?.(scannedCode);
		// scanner is stopped automatically --> reset scannerHandle
		this.scannerHandle = undefined;
		this.modal.close();
	}

	private show() {
		this.modal = new BootstrapModal.Modal({
			message: $(this),
			title: "QR Code Scanner",
			destroyOnClose: true,
			onClose: () => {
				// stop scanner in case dialog was closed without a QR Code being scanned (e.g. pressing ESC, clicking top right 'x' or the cancel button)
				// the handle may be undefined here if the scanner was already stopped or hasn't even been started
				this.scannerHandle?.stop();
			},
			buttons: [
				{
					text: "dialog.cancel",
					css: "cancelButton btn-default"
				}
			]
		});
		this.modal.show();
	}
}
